/**
 * Provocations: concrete-target challenges that invite a direct counter (replies are
 * the highest-weighted X signal — IDEA.md). NO engagement farming (no follow/RT/tag).
 * Cocky/direct register. The big number already sits above the provocation on the card,
 * so the line itself does NOT repeat the number (avoids "66ms / 66ms. ...").
 */
const REFLEX_PROVOCATIONS = [
  "bet none of you beat it",
  "beat this or stay slow",
  "your move",
  "go on, try me",
  "nobody's catching this",
] as const;

/**
 * Pick a provocation for a finished run. Deterministic on the value so the same score
 * always shows the same line (no Math.random — keeps renders reproducible).
 * `display` is unused now but kept in the signature for callers / future templating.
 */
export function reflexProvocation(_display: string, value: number): string {
  const i = Math.abs(value) % REFLEX_PROVOCATIONS.length;
  return REFLEX_PROVOCATIONS[i]!;
}

/** Line for the OG card when no one has set a score yet (empty leaderboard). */
export const REFLEX_EMPTY_PROVOCATION = "no one's set a time yet. be the first.";

/**
 * Wheel (daily streak) provocations — discipline/chain register. The streak number
 * already sits above this line on the card, so the line does NOT repeat it.
 */
const WHEEL_PROVOCATIONS = [
  "miss a day and it's gone",
  "don't break it",
  "keep up or fall off",
  "every day. no excuses",
  "bet you can't keep this",
] as const;

export function wheelProvocation(streak: number): string {
  const i = Math.abs(streak) % WHEEL_PROVOCATIONS.length;
  return WHEEL_PROVOCATIONS[i]!;
}

/** Lines when a spin hit a multiplier (x2 / x3 jackpot) — flexing the gamble win. */
const WHEEL_MULTIPLIER_PROVOCATIONS = [
  "the wheel loves me",
  "gambled and won. you?",
  "multiplied. catch up",
  "house money now",
] as const;

export function wheelMultiplierProvocation(streak: number): string {
  const i = Math.abs(streak) % WHEEL_MULTIPLIER_PROVOCATIONS.length;
  return WHEEL_MULTIPLIER_PROVOCATIONS[i]!;
}

/** Lines for a bust (lost the streak) — posting the L for laughs. */
const WHEEL_BUST_PROVOCATIONS = [
  "lost it all. starting over",
  "the wheel giveth, the wheel taketh",
  "back to zero. worth it",
  "gambled it away. lol",
] as const;

export function wheelBustProvocation(lostStreak: number): string {
  const i = Math.abs(lostStreak) % WHEEL_BUST_PROVOCATIONS.length;
  return WHEEL_BUST_PROVOCATIONS[i]!;
}

/** OG card line when no one has a streak yet. */
export const WHEEL_EMPTY_PROVOCATION = "no streaks yet. start one.";

/**
 * Aim-trainer provocations — cocky, beat-my-score register. The hit count already sits
 * above this line on the card, so the line does NOT repeat it.
 */
const AIM_PROVOCATIONS = [
  "beat that. i dare you",
  "slow hands lose",
  "your aim is mid",
  "catch up if you can",
  "click faster",
] as const;

export function aimProvocation(score: number): string {
  const i = Math.abs(score) % AIM_PROVOCATIONS.length;
  return AIM_PROVOCATIONS[i]!;
}

/** OG card line when no one has an aim score yet. */
export const AIM_EMPTY_PROVOCATION = "no scores yet. set the bar.";

/** Crash WIN provocations (cashed out) — cocky, beat-my-multiplier register. */
const CRASH_PROVOCATIONS = [
  "cashed out. did you?",
  "nerves of steel",
  "beat that multiplier",
  "diamond hands",
  "got out in time",
] as const;

export function crashProvocation(scoreValue: number): string {
  const i = Math.abs(scoreValue) % CRASH_PROVOCATIONS.length;
  return CRASH_PROVOCATIONS[i]!;
}

/** Crash LOSS provocations (crashed before cashing) — posting the L. */
const CRASH_BUST_PROVOCATIONS = [
  "held too long. classic",
  "greedy. it crashed",
  "should've cashed out",
  "paper hands punished",
] as const;

export function crashBustProvocation(scoreValue: number): string {
  const i = Math.abs(scoreValue) % CRASH_BUST_PROVOCATIONS.length;
  return CRASH_BUST_PROVOCATIONS[i]!;
}

/** OG card line when no one has a crash score yet. */
export const CRASH_EMPTY_PROVOCATION = "no one's cashed out yet. go.";

/**
 * Stop-at-5.00s provocations — internal-clock flex register. The miss distance already
 * sits above this line on the card, so the line does NOT repeat it.
 */
const STOP_PROVOCATIONS = [
  "get closer. i dare you",
  "your internal clock is broken",
  "5 seconds. how hard can it be",
  "blind after 2s. no excuses",
  "beat this without counting",
] as const;

export function stopProvocation(deltaMs: number): string {
  const i = Math.abs(deltaMs) % STOP_PROVOCATIONS.length;
  return STOP_PROVOCATIONS[i]!;
}

/** Line for a frame-perfect 0ms stop — rare enough to deserve its own flex. */
export const STOP_PERFECT_PROVOCATION = "frame perfect. don't bother";

/** OG card line when no one has stopped the clock yet. */
export const STOP_EMPTY_PROVOCATION = "no one's stopped the clock yet. first.";

/** Mines WIN provocations (cashed off the board) — cool-hands register. */
const MINES_PROVOCATIONS = [
  "walked the minefield. your turn",
  "cold hands, clean sweep",
  "the mines blinked first",
  "beat that without exploding",
  "tiptoed out rich",
] as const;

export function minesProvocation(scoreValue: number): string {
  const i = Math.abs(scoreValue) % MINES_PROVOCATIONS.length;
  return MINES_PROVOCATIONS[i]!;
}

/** Mines BUST provocations — posting the boom. */
const MINES_BUST_PROVOCATIONS = [
  "one more tile. classic",
  "the greed tax is real",
  "stepped right on it. lol",
  "should've cashed at 2x",
] as const;

export function minesBustProvocation(gems: number): string {
  const i = Math.abs(gems) % MINES_BUST_PROVOCATIONS.length;
  return MINES_BUST_PROVOCATIONS[i]!;
}

/** OG card line when no one has swept the minefield yet. */
export const MINES_EMPTY_PROVOCATION = "minefield's untouched. sweep it.";

/** Limbo provocations (the call landed) — ice-water register. */
const LIMBO_PROVOCATIONS = [
  "call it higher. i dare you",
  "named it before it rolled",
  "your nerve runs out before mine",
  "scared money calls 1.5x",
  "the dice respect confidence",
] as const;

export function limboProvocation(scoreValue: number): string {
  const i = Math.abs(scoreValue) % LIMBO_PROVOCATIONS.length;
  return LIMBO_PROVOCATIONS[i]!;
}

/** OG card line when no one has landed a call yet. */
export const LIMBO_EMPTY_PROVOCATION = "no calls landed yet. name your number.";

/** Plinko provocations — lottery-luck register. */
const PLINKO_PROVOCATIONS = [
  "the ball chose me",
  "gravity's on my side",
  "drop one. coward",
  "edge bucket or nothing",
  "physics owes you nothing",
] as const;

export function plinkoProvocation(scoreValue: number): string {
  const i = Math.abs(scoreValue) % PLINKO_PROVOCATIONS.length;
  return PLINKO_PROVOCATIONS[i]!;
}

/** OG card line when nobody has hit a real bucket yet. */
export const PLINKO_EMPTY_PROVOCATION = "no big buckets hit yet. drop one.";

/** Draw provocations — prophet register. The shared ritual is the hook. */
const DRAW_PROVOCATIONS = [
  "saw it coming. again",
  "the numbers talk to me",
  "tomorrow, 19:00. watch me again",
  "luck is a skill, apparently",
  "called it in front of everyone",
] as const;

export function drawProvocation(streak: number): string {
  const i = Math.abs(streak) % DRAW_PROVOCATIONS.length;
  return DRAW_PROVOCATIONS[i]!;
}

/** OG card line when nobody has called a draw yet. */
export const DRAW_EMPTY_PROVOCATION = "nobody's called one yet. 19:00 UTC daily.";

/** Rocket provocations — survivors-club register (same flight, better nerves). */
const ROCKET_PROVOCATIONS = [
  "same rocket. better nerves",
  "survivors club, members: me",
  "we boarded together. i landed richer",
  "watched the rest of you burn",
  "next flight in 10 — board it",
] as const;

export function rocketProvocation(scoreValue: number): string {
  const i = Math.abs(scoreValue) % ROCKET_PROVOCATIONS.length;
  return ROCKET_PROVOCATIONS[i]!;
}

/** OG card line when no flight has survivors yet. */
export const ROCKET_EMPTY_PROVOCATION = "no survivors yet. next flight in 10.";
